import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLocation } from "react-router-dom";
import { bereavementsApi, contributionsApi, paymentApi, financeApi, settingsApi, walletApi } from "@/lib/api";
import { ArrowLeft, CalendarDots, CheckCircle, Phone, UsersThree, HandCoins, Wallet } from "@phosphor-icons/react";
import { format } from "date-fns";
import { toast } from "sonner";
import { bereavementSubtitle } from "@/lib/bereavement";

const FINANCE_ROLES = new Set(["super_admin", "admin", "treasurer"]);

export default function BereavementDetail({ bereavementId, onBack }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [selectedLinked, setSelectedLinked] = useState({});
  const [paymentMethod, setPaymentMethod] = useState("mock");
  const pollTimerRef = useRef(null);

  const fetchDetail = () => {
    bereavementsApi.get(bereavementId).then(({ data }) => {
      setData(data);
      setLoading(false);
      const sel = {};
      (data.linkedAccounts || []).forEach(la => { if (!la.paid) sel[la.id] = true; });
      setSelectedLinked(sel);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { fetchDetail(); }, [bereavementId]); // eslint-disable-line

  useEffect(() => {
    paymentApi.method().then(({ data }) => setPaymentMethod(data?.method || "mock")).catch(() => setPaymentMethod("mock"));
    return () => { if (pollTimerRef.current) clearInterval(pollTimerRef.current); };
  }, []);

  const pollStkUntilDone = (checkoutRequestId) => {
    let elapsed = 0;
    const intervalMs = 4000;
    const maxMs = 90_000; // 90s — Daraja typically resolves within 30s
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    pollTimerRef.current = setInterval(async () => {
      elapsed += intervalMs;
      try {
        const { data: s } = await contributionsApi.stkStatus(checkoutRequestId);
        if (s.status === "success") {
          clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
          toast.success(`Payment confirmed! Receipt ${s.mpesaReceiptNumber || "—"}`);
          setPaying(false);
          fetchDetail();
        } else if (s.status === "failed") {
          clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
          toast.error(s.resultDesc || s.error || "Payment was cancelled or failed");
          setPaying(false);
        }
      } catch {
        // soft-fail; keep polling
      }
      if (elapsed >= maxMs && pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
        toast.warning("Still waiting for M-Pesa confirmation. Refresh once your phone confirms.");
        setPaying(false);
      }
    }, intervalMs);
  };

  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showPayoutDialog, setShowPayoutDialog] = useState(false);
  const [standardBenefit, setStandardBenefit] = useState(120000);
  const [walletBalance, setWalletBalance] = useState(0);
  const location = useLocation();
  const inAdminView = location.pathname.startsWith("/admin");
  // Mark-payout is an admin-only action AND should never appear in the member
  // view of the app (even when the logged-in user is also a finance role).
  const canManagePayouts = inAdminView && FINANCE_ROLES.has(user?.role);

  useEffect(() => {
    if (!canManagePayouts) return;
    settingsApi.get()
      .then(({ data }) => setStandardBenefit(Number(data?.standardBenefitAmount) || 120000))
      .catch(() => {});
  }, [canManagePayouts]);

  // Member-side: load wallet balance to offer "Pay from wallet" when sufficient.
  useEffect(() => {
    if (inAdminView) return;
    walletApi.me().then(({ data }) => setWalletBalance(Number(data?.balance) || 0)).catch(() => {});
  }, [inAdminView]);

  const handleWalletPay = async () => {
    setPaying(true);
    try {
      await walletApi.pay(bereavementId);
      toast.success("Paid from wallet ✓");
      // Refresh both wallet & bereavement
      walletApi.me().then(({ data }) => setWalletBalance(Number(data?.balance) || 0)).catch(() => {});
      fetchDetail();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wallet payment failed");
    } finally {
      setPaying(false);
    }
  };

  const handleContribute = async () => {
    if (!data?.bereavement) return;
    const linkedIds = Object.keys(selectedLinked).filter(id => selectedLinked[id]);
    const payerIds = [];
    if (!data.currentUserPaid && user?.id) payerIds.push(user.id);
    payerIds.push(...linkedIds);
    if (payerIds.length === 0) { toast.warning("Nothing to pay for"); return; }

    if (paymentMethod === "mpesa") {
      setPaying(true);
      try {
        const { data: result } = await contributionsApi.stkPush({ bereavementId, memberIds: payerIds });
        toast.info(result.message || "Check your phone — STK prompt sent");
        if (result.checkoutRequestId) pollStkUntilDone(result.checkoutRequestId);
        else { setPaying(false); fetchDetail(); }
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Payment failed");
        setPaying(false);
      }
      return;
    }

    // M-Pesa not configured → open the "Submit external payment reference" dialog
    setShowSubmitDialog(true);
  };

  const submitReference = async (form) => {
    const linkedIds = Object.keys(selectedLinked).filter(id => selectedLinked[id]);
    const payerIds = [];
    if (!data.currentUserPaid && user?.id) payerIds.push(user.id);
    payerIds.push(...linkedIds);
    setPaying(true);
    try {
      await contributionsApi.submitReference({
        bereavementId,
        memberIds: payerIds,
        paymentMethod: form.method,
        reference: form.reference,
        paidAt: form.paidAt,
        notes: form.notes,
      });
      toast.success("Submitted for treasurer review — you'll see it on your statement once approved.");
      setShowSubmitDialog(false);
      fetchDetail();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit reference");
    } finally {
      setPaying(false);
    }
  };

  const releasePayout = async (form) => {
    setPaying(true);
    try {
      await financeApi.createPayout({
        bereavementId,
        amount: form.amount,
        date: form.date,
        paidTo: form.paidTo,
        paidVia: form.paidVia,
        description: form.description,
      });
      toast.success(`Payout of KES ${form.amount.toLocaleString()} logged to ${form.paidTo}`);
      setShowPayoutDialog(false);
      fetchDetail();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not record payout");
    } finally {
      setPaying(false);
    }
  };

  if (loading) return <div className="p-6"><div className="h-48 bg-muted rounded-xl animate-pulse" /></div>;
  if (!data) return <div className="p-6 text-center text-muted-foreground">Not found</div>;

  const { bereavement: b, contributions, totalMembers, paidCount, unpaidCount, currentUserPaid, linkedAccounts = [] } = data;
  const unpaidLinked = linkedAccounts.filter(la => !la.paid);
  const selectedCount = Object.values(selectedLinked).filter(Boolean).length;
  const selfNeedsPay = b.status === "active" && !currentUserPaid;
  const totalToPay = ((selfNeedsPay ? 1 : 0) + selectedCount) * b.contributionAmount;

  return (
    <div className="min-h-screen bg-background" data-testid="bereavement-detail">
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border px-4 py-3 flex items-center gap-3">
        <button onClick={onBack} className="p-1.5 rounded-lg hover:bg-secondary" data-testid="bereavement-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="font-heading text-lg font-semibold truncate">{b.deceasedName}</h1>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
        {/* Info card */}
        <div className="bg-card border border-border rounded-xl p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-secondary">{b.relationship}</span>
            {b.status === "active" ? (
              <span className="flex items-center gap-1 text-xs font-medium text-green-700"><span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Active</span>
            ) : (
              <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">Closed</span>
            )}
          </div>
          <div className="flex items-start gap-4">
            {b.imageUrl && (
              <img
                src={b.imageUrl}
                alt={b.deceasedName}
                className="w-24 h-24 sm:w-32 sm:h-32 rounded-xl object-cover bg-muted border border-border shrink-0"
                data-testid="bereavement-hero-image"
              />
            )}
            <div className="min-w-0 flex-1">
              <h2 className="font-heading text-2xl font-semibold tracking-tight">{b.deceasedName}</h2>
              <p className="text-muted-foreground text-sm mt-1">{bereavementSubtitle(b)}</p>
              {inAdminView && (user?.role === "super_admin" || user?.role === "treasurer" || user?.role === "admin") && (
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/exports/bereavement/${bereavementId}/contributors.csv`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="download-contributors-csv"
                  className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-lg bg-secondary text-foreground border border-border text-xs font-semibold hover:bg-secondary/70"
                >
                  Download contributors (CSV)
                </a>
              )}
            </div>
          </div>
          {b.description && <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-line">{b.description}</p>}
          <div className="flex gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1"><CalendarDots className="w-4 h-4" />{b.dateOfDeath ? format(new Date(b.dateOfDeath), "dd MMM yyyy") : "—"}</span>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-card border border-border rounded-xl p-4 text-center">
            <p className="text-xl font-bold text-primary font-body">KES {(b.totalContributed || 0).toLocaleString()}</p>
            <p className="text-xs text-muted-foreground mt-0.5">Collected</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4 text-center">
            <p className="text-xl font-bold text-foreground font-body">{paidCount}</p>
            <p className="text-xs text-muted-foreground mt-0.5">Paid</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4 text-center">
            <p className="text-xl font-bold text-accent font-body">{unpaidCount}</p>
            <p className="text-xs text-muted-foreground mt-0.5">Unpaid</p>
          </div>
        </div>

        {/* Financial summary — collected / disbursed / balance */}
        {(b.totalDisbursed > 0 || b.balance !== undefined) && (
          <div className="bg-card border border-border rounded-xl p-5 space-y-3" data-testid="bereavement-financials">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="font-heading font-semibold text-sm">Financial summary</p>
              <div className="flex flex-wrap gap-2">
                {canManagePayouts && (
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/exports/bereavement/${bereavementId}/contributors.csv`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="download-contributors-csv-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-secondary text-foreground border border-border text-xs font-semibold active:scale-95 transition-transform"
                  >
                    Download contributors (CSV)
                  </a>
                )}
                {canManagePayouts && (
                  <button
                    onClick={() => setShowPayoutDialog(true)}
                    data-testid="mark-payout-released-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold active:scale-95 transition-transform"
                  >
                    <HandCoins className="w-3.5 h-3.5" weight="fill" /> Mark payout as released
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <p className="text-xs text-muted-foreground">Collected</p>
                <p className="font-heading text-lg font-bold text-emerald-700">KES {(b.totalCollected || b.totalContributed || 0).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Disbursed</p>
                <p className="font-heading text-lg font-bold text-orange-600">KES {(b.totalDisbursed || 0).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Balance</p>
                <p className={`font-heading text-lg font-bold ${(b.balance || 0) < 0 ? "text-red-700" : "text-primary"}`}>KES {(b.balance || 0).toLocaleString()}</p>
              </div>
            </div>
            {(b.payouts && b.payouts.length > 0) && (
              <div className="border-t border-border pt-3 space-y-1">
                <p className="text-xs font-semibold text-muted-foreground">Disbursements</p>
                {b.payouts.map((p, i) => (
                  <div key={`po-${i}`} className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Payout {p.paidTo ? `to ${p.paidTo}` : ""} {p.date && `· ${p.date}`}</span>
                    <span className="font-mono font-semibold text-orange-700">KES {p.amount.toLocaleString()}</span>
                  </div>
                ))}
                {b.expenses && b.expenses.map((e, i) => (
                  <div key={`ex-${i}`} className="flex justify-between text-xs">
                    <span className="text-muted-foreground">{e.description || "Expense"} {e.date && `· ${e.date}`}</span>
                    <span className="font-mono font-semibold text-red-600">KES {e.amount.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Linked Accounts Payment Section */}
        {b.status === "active" && unpaidLinked.length > 0 && !currentUserPaid && (
          <div className="bg-card border border-border rounded-xl p-5 shadow-sm space-y-3" data-testid="linked-accounts-section">
            <div className="flex items-center gap-2">
              <UsersThree className="w-5 h-5 text-primary" weight="duotone" />
              <h3 className="font-heading font-semibold text-sm">Pay for linked accounts too</h3>
            </div>
            <p className="text-xs text-muted-foreground">Select which linked accounts to include in this payment.</p>
            <div className="space-y-2">
              {unpaidLinked.map(la => (
                <label key={la.id} className="flex items-center gap-3 bg-secondary/30 rounded-lg p-3 cursor-pointer hover:bg-secondary/50 transition-colors">
                  <input
                    type="checkbox"
                    checked={!!selectedLinked[la.id]}
                    onChange={(e) => setSelectedLinked(prev => ({ ...prev, [la.id]: e.target.checked }))}
                    className="w-4 h-4 rounded border-border accent-primary"
                    data-testid={`linked-check-${la.id}`}
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium">{la.name}</p>
                    <p className="text-xs text-muted-foreground">{la.memberNumber}</p>
                  </div>
                  <span className="text-sm font-body font-semibold text-primary">KES {b.contributionAmount}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Payment summary + button */}
        {b.status === "active" && (selfNeedsPay || selectedCount > 0) && (
          <div className="space-y-3">
            {(selfNeedsPay && selectedCount > 0) && (
              <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 text-center">
                <p className="text-sm text-muted-foreground">Payment Summary</p>
                <p className="text-2xl font-bold text-primary font-body mt-1">KES {totalToPay.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {selfNeedsPay ? "You" : ""}{selfNeedsPay && selectedCount > 0 ? " + " : ""}{selectedCount > 0 ? `${selectedCount} linked account(s)` : ""}
                  {" "}× KES {b.contributionAmount}
                </p>
              </div>
            )}
            <button
              onClick={handleContribute}
              disabled={paying}
              data-testid="contribute-button"
              className="w-full py-4 rounded-xl bg-primary text-primary-foreground font-semibold text-base flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60"
            >
              {paying ? (
                paymentMethod === "mpesa" ? "Waiting for M-Pesa…" : "Processing..."
              ) : (
                <><Phone className="w-5 h-5" /> {paymentMethod === "mpesa" ? "Pay" : "Submit Payment"} KES {totalToPay > 0 ? totalToPay.toLocaleString() : b.contributionAmount} {paymentMethod === "mpesa" && <span className="text-xs opacity-75">via M-Pesa</span>}</>
              )}
            </button>

            {/* Wallet payment option — covers self only (KES contributionAmount). */}
            {!inAdminView && selfNeedsPay && (
              <button
                onClick={handleWalletPay}
                disabled={paying || walletBalance < b.contributionAmount}
                data-testid="contribute-wallet-button"
                className="w-full py-3 rounded-xl bg-white border border-emerald-300 text-emerald-700 font-semibold text-sm flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50"
              >
                <Wallet className="w-4 h-4" weight="fill" />
                {walletBalance >= b.contributionAmount
                  ? <>Pay KES {b.contributionAmount} from Wallet <span className="text-xs opacity-75">· balance KES {Math.round(walletBalance).toLocaleString()}</span></>
                  : <>Wallet balance too low (KES {Math.round(walletBalance).toLocaleString()})</>
                }
              </button>
            )}
          </div>
        )}

        {/* Linked accounts that are already paid */}
        {linkedAccounts.some(la => la.paid) && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-2">
            <p className="text-sm font-semibold text-green-700">Linked accounts - paid</p>
            {linkedAccounts.filter(la => la.paid).map(la => (
              <div key={la.id} className="flex items-center gap-2 text-sm text-green-600">
                <CheckCircle className="w-4 h-4" weight="fill" /> {la.name} ({la.memberNumber})
              </div>
            ))}
          </div>
        )}

        {currentUserPaid && selectedCount === 0 && (
          <div className="flex items-center gap-3 rounded-xl p-4 bg-green-50 border border-green-200" data-testid="already-paid-badge">
            <CheckCircle className="w-6 h-6 text-green-600" weight="fill" />
            <p className="font-semibold text-green-700 text-sm">You have contributed to this bereavement</p>
          </div>
        )}

        {/* Contributors list */}
        {contributions.length > 0 && (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-border bg-secondary/30">
              <p className="font-heading font-semibold text-sm">Contributors ({contributions.length})</p>
            </div>
            <div className="divide-y divide-border">
              {contributions.map((c, i) => (
                <div key={i} className="px-5 py-3 flex items-center justify-between text-sm">
                  <div>
                    <p className="font-medium">{c.memberName}</p>
                    <p className="text-xs text-muted-foreground">{c.memberNumber}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold font-body text-primary">KES {c.amount}</p>
                    {c.paidAt && <p className="text-xs text-muted-foreground">{format(new Date(c.paidAt), "dd MMM")}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {showSubmitDialog && (
        <SubmitReferenceDialog
          totalAmount={totalToPay}
          memberCount={(data.currentUserPaid ? 0 : 1) + selectedCount}
          onCancel={() => setShowSubmitDialog(false)}
          onSubmit={submitReference}
          submitting={paying}
        />
      )}

      {showPayoutDialog && (
        <MarkPayoutDialog
          deceasedName={b.deceasedName}
          standardBenefit={standardBenefit}
          balance={Number(b.balance || 0)}
          alreadyDisbursed={Number(b.totalDisbursed || 0)}
          onCancel={() => setShowPayoutDialog(false)}
          onSubmit={releasePayout}
          submitting={paying}
        />
      )}
    </div>
  );
}

// ─── Submit External Payment Reference Dialog ────────────────────────────────
function SubmitReferenceDialog({ totalAmount, memberCount, onCancel, onSubmit, submitting }) {
  const [method, setMethod] = useState("mpesa");
  const [reference, setReference] = useState("");
  const [paidAt, setPaidAt] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 16); // YYYY-MM-DDTHH:MM for datetime-local
  });
  const [notes, setNotes] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (method !== "cash" && !reference.trim()) {
      toast.error("Reference is required");
      return;
    }
    onSubmit({
      method,
      reference: reference.trim(),
      paidAt: paidAt ? new Date(paidAt).toISOString() : new Date().toISOString(),
      notes: notes.trim(),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="submit-reference-dialog">
      <div className="bg-card rounded-xl border border-border w-full max-w-md p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div>
          <h3 className="font-heading text-lg font-semibold">Submit your payment</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Send <strong>KES {totalAmount.toLocaleString()}</strong> via M-Pesa or bank, then enter the reference below for the treasurer to verify {memberCount > 1 ? `your ${memberCount} contributions` : "your contribution"}.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs font-medium text-foreground block mb-1.5">Payment method</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: "mpesa", l: "M-Pesa" },
                { v: "bank", l: "Bank" },
                { v: "cash", l: "Cash" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setMethod(opt.v)}
                  data-testid={`payment-method-${opt.v}`}
                  className={`py-2 rounded-xl border text-sm font-medium transition-colors ${
                    method === opt.v
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-card text-foreground border-input hover:bg-secondary"
                  }`}
                >
                  {opt.l}
                </button>
              ))}
            </div>
          </div>

          {method !== "cash" && (
            <div>
              <label className="text-xs font-medium text-foreground block mb-1.5">
                {method === "mpesa" ? "M-Pesa transaction code" : "Bank reference number"} *
              </label>
              <input
                value={reference}
                onChange={(e) => setReference(e.target.value.toUpperCase())}
                placeholder={method === "mpesa" ? "RGA8K2X9LM" : "Bank ref / slip number"}
                data-testid="payment-reference-input"
                className="w-full px-3 py-2.5 text-sm bg-card border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring font-mono"
                required
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                {method === "mpesa" && "Find this in your M-Pesa SMS confirmation."}
                {method === "bank" && "Slip number, RTGS ref, or PesaLink ref."}
              </p>
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-foreground block mb-1.5">Payment date & time</label>
            <input
              type="datetime-local"
              value={paidAt}
              onChange={(e) => setPaidAt(e.target.value)}
              data-testid="payment-paid-at-input"
              className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-foreground block mb-1.5">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="e.g., 'Sent from my Safaricom 0728...'"
              className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              data-testid="payment-notes-input"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <button
              type="submit"
              disabled={submitting}
              data-testid="submit-payment-reference-btn"
              className="flex-1 py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm active:scale-95 disabled:opacity-60"
            >
              {submitting ? "Submitting…" : "Submit for review"}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className="px-5 py-2.5 rounded-xl bg-secondary text-secondary-foreground border border-border font-medium text-sm"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// ─── Mark Payout As Released Dialog (treasurer / admin / super admin) ────────
function MarkPayoutDialog({ deceasedName, standardBenefit, balance, alreadyDisbursed, onCancel, onSubmit, submitting }) {
  const remainingToStandard = Math.max(0, standardBenefit - alreadyDisbursed);
  const defaultAmount = remainingToStandard > 0 ? remainingToStandard : balance;
  const [amount, setAmount] = useState(defaultAmount > 0 ? String(defaultAmount) : "");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [paidTo, setPaidTo] = useState("");
  const [paidVia, setPaidVia] = useState("bank");
  const [description, setDescription] = useState("");

  const submit = (e) => {
    e.preventDefault();
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error("Enter a positive amount"); return; }
    if (!paidTo.trim()) { toast.error("Recipient name is required"); return; }
    onSubmit({
      amount: amt,
      date,
      paidTo: paidTo.trim(),
      paidVia,
      description: description.trim(),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="mark-payout-dialog">
      <div className="bg-card rounded-xl border border-border w-full max-w-md p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div>
          <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
            <HandCoins className="w-5 h-5 text-primary" weight="fill" /> Release benefit payout
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            Log a disbursement to <strong>{deceasedName}</strong>'s family. Standard benefit is
            <strong> KES {standardBenefit.toLocaleString()}</strong>
            {alreadyDisbursed > 0 && <> (already disbursed: KES {alreadyDisbursed.toLocaleString()})</>}.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs font-medium text-foreground block mb-1.5">Amount (KES) *</label>
            <input
              type="number" min="1" step="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="payout-amount-input"
              className="w-full px-3 py-2.5 text-sm bg-card border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring font-mono"
              required
            />
          </div>

          <div>
            <label className="text-xs font-medium text-foreground block mb-1.5">Recipient *</label>
            <input
              value={paidTo}
              onChange={(e) => setPaidTo(e.target.value)}
              placeholder="e.g. John Kamau (next of kin)"
              data-testid="payout-paid-to-input"
              className="w-full px-3 py-2.5 text-sm bg-card border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-foreground block mb-1.5">Date *</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="payout-date-input"
                className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring"
                required
              />
            </div>
            <div>
              <label className="text-xs font-medium text-foreground block mb-1.5">Method</label>
              <select
                value={paidVia}
                onChange={(e) => setPaidVia(e.target.value)}
                data-testid="payout-method-select"
                className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="bank">Bank transfer</option>
                <option value="mpesa">M-Pesa</option>
                <option value="cash">Cash</option>
                <option value="cheque">Cheque</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-foreground block mb-1.5">Notes (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Bank ref, cheque number, witnesses, etc."
              data-testid="payout-description-input"
              className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <button
              type="submit"
              disabled={submitting}
              data-testid="payout-submit-btn"
              className="flex-1 py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm active:scale-95 disabled:opacity-60"
            >
              {submitting ? "Logging…" : "Log payout"}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              data-testid="payout-cancel-btn"
              className="px-5 py-2.5 rounded-xl bg-secondary text-secondary-foreground border border-border font-medium text-sm"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
